import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
  AppCheck,
} from 'firebase/app-check';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  Firestore,
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  Functions,
} from 'firebase/functions';

import { isProduction } from '@j-s/helpers';

const firebaseConfig = {
  apiKey: 'AIzaSyCGpkXhwtWPLzTOD3MRH15w40aaVo4iAF0',
  authDomain: 'domrowerowy-75c4c.firebaseapp.com',
  projectId: 'domrowerowy-75c4c',
  storageBucket: 'domrowerowy-75c4c.firebasestorage.app',
  messagingSenderId: '700103515160',
  appId: '1:700103515160:web:5148a214c2622e8d37376d',
  measurementId: 'G-24D5DDVFW1',
};

const app: FirebaseApp =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const db: Firestore = getFirestore(app);
const functions: Functions = getFunctions(app, 'europe-central2');

let authInstance: Auth | null = null;
let appCheckInstance: AppCheck | null = null;

if (typeof window !== 'undefined') {
  const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  if (reCaptchaKey) {
    try {
      appCheckInstance = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(reCaptchaKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      console.warn('App Check initialization error:', err);
    }
  } else if (isProduction) {
    console.error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not defined');
  }
}

if (process.env.NODE_ENV === 'development' && !isProduction) {
  try {
    connectFunctionsEmulator(functions, '127.0.0.1', 9096);
    connectFirestoreEmulator(db, '127.0.0.1', 9098);
  } catch (err) {
    console.warn('Firebase Emulator connection failed', err);
  }
}

export function getFirebaseApp(): FirebaseApp {
  return app;
}

export function getFirebaseFirestore(): Firestore {
  return db;
}

export function getFirebaseFunctions(): Functions {
  return functions;
}

export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    authInstance = getAuth(app);
    if (process.env.NODE_ENV === 'development' && !isProduction) {
      try {
        connectAuthEmulator(authInstance, 'http://127.0.0.1:9099');
      } catch (err) {
        console.warn('Auth Emulator connection failed', err);
      }
    }
  }

  return authInstance;
}

export function getFirebaseAppCheck(): AppCheck | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!appCheckInstance) {
    const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

    if (reCaptchaKey) {
      try {
        appCheckInstance = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(reCaptchaKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (err) {
        console.warn('App Check initialization error:', err);
      }
    } else if (isProduction) {
      console.error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not defined');
    }
  }

  return appCheckInstance;
}

export async function getAppCheckToken(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const appCheck = getFirebaseAppCheck();

  if (!appCheck) {
    // In dev / test environment where reCAPTCHA key is not set
    if (process.env.NODE_ENV === 'development' || !isProduction) {
      return 'dev-verified-app-check-token';
    }

    return null;
  }

  try {
    const result = await getToken(appCheck, false);

    return result.token;
  } catch (err) {
    console.error('Failed to get App Check token:', err);

    return null;
  }
}

// Lazy auth getter for compatibility
export const auth = new Proxy({} as Auth, {
  get(_, prop) {
    const instance = getFirebaseAuth();
    const value = (instance as any)[prop];

    if (typeof value === 'function') {
      return value.bind(instance);
    }

    return value;
  },
});

export { app, db, functions };
